import { useState } from "react";
import {
  useQuery as useConvexQuery,
  useMutation as useConvexMutation,
} from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useToast } from "@/hooks/use-toast";

export interface PortfolioImage {
  storageId: string;
  displayOrder: number;
  url: string;
}

export interface PortfolioProject {
  id: string;
  name: string;
  description: string;
  coverImageUrl: string;
  coverImageStorageId: string;
  displayOrder: number;
  galleryImages: string[];
  images: PortfolioImage[];
  createdAt: string;
  updatedAt: string;
}

type RawPortfolioDate = number | string | undefined;

interface RawPortfolioImage {
  storageId?: string;
  displayOrder?: number;
  url?: string;
}

interface RawPortfolioProject {
  _id?: string;
  id?: string;
  name?: string;
  description?: string;
  coverImageUrl?: string;
  coverImageStorageId?: string;
  displayOrder?: number;
  galleryImages?: unknown[];
  images?: RawPortfolioImage[];
  _creationTime?: RawPortfolioDate;
  createdAt?: RawPortfolioDate;
  updatedAt?: RawPortfolioDate;
}

/**
 * Normalize a Convex portfolio project document to the client PortfolioProject interface.
 */
function normalizeProject(p: RawPortfolioProject): PortfolioProject {
  const rawCreated = p._creationTime || p.createdAt;
  const createdAt =
    typeof rawCreated === "number"
      ? new Date(rawCreated).toISOString()
      : String(rawCreated || "");

  // Convex doesn't have updatedAt by default, use _creationTime as fallback
  const rawUpdated = p.updatedAt || rawCreated;
  const updatedAt =
    typeof rawUpdated === "number"
      ? new Date(rawUpdated).toISOString()
      : String(rawUpdated || "");

  return {
    id: p._id || p.id || "",
    name: p.name || "",
    description: p.description || "",
    coverImageUrl: p.coverImageUrl || "",
    coverImageStorageId: p.coverImageStorageId || "",
    displayOrder: p.displayOrder ?? 0,
    galleryImages: (p.galleryImages || []).filter((img): img is string => typeof img === "string" && img.length > 0),
    images: (p.images || []).map((img) => ({
      storageId: img.storageId || "",
      displayOrder: img.displayOrder ?? 0,
      url: img.url || "",
    })),
    createdAt,
    updatedAt,
  };
}

/**
 * Public hook for fetching portfolio projects (landing page).
 * Uses Convex reactive query (auto-updates).
 */
export function usePortfolioProjects() {
  const rawData = useConvexQuery(api.portfolio.list, {});

  const normalizedProjects =
    rawData !== undefined
      ? rawData.projects.map(normalizeProject)
      : undefined;

  return {
    data: normalizedProjects ? { projects: normalizedProjects } : undefined,
    isLoading: normalizedProjects === undefined,
    error: null as Error | null,
  };
}

/**
 * Hook for fetching a single portfolio project.
 * Uses Convex reactive query.
 */
export function usePortfolioProject(id: string) {
  const rawData = useConvexQuery(
    api.portfolio.get,
    id ? { id: id as Id<"portfolioProjects"> } : "skip"
  );

  const normalizedProject =
    rawData !== undefined ? normalizeProject(rawData.project) : undefined;

  return {
    data: normalizedProject ? { project: normalizedProject } : undefined,
    isLoading: normalizedProject === undefined,
    error: null as Error | null,
  };
}

/**
 * Admin hook for creating portfolio projects.
 * Uses Convex mutation.
 */
export function useCreatePortfolioProject() {
  const createMutation = useConvexMutation(api.portfolio.create);
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);

  return {
    mutateAsync: async (data: {
      name: string;
      description: string;
      coverImageStorageId: Id<"_storage">;
      displayOrder?: number;
      additionalImages?: Array<{ storageId: Id<"_storage">; displayOrder: number }>;
    }) => {
      setIsPending(true);
      try {
        const id = await createMutation(data);
        toast({
          title: "Project created",
          description: "Portfolio project has been added.",
        });
        return { id };
      } catch (error) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to create project",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    isPending,
  };
}

/**
 * Admin hook for deleting portfolio projects.
 * Uses Convex mutation.
 */
export function useDeletePortfolioProject() {
  const removeMutation = useConvexMutation(api.portfolio.remove);
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);

  return {
    mutateAsync: async (projectId: string) => {
      setIsPending(true);
      try {
        await removeMutation({ id: projectId as Id<"portfolioProjects"> });
        toast({
          title: "Project deleted",
          description: "Portfolio project has been removed.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete project.",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    isPending,
  };
}

/**
 * Admin hook for updating portfolio projects.
 * Uses Convex mutation.
 */
export function useUpdatePortfolioProject() {
  const updateMutation = useConvexMutation(api.portfolio.update);
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);

  return {
    mutateAsync: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        description?: string;
        displayOrder?: number;
        coverImageStorageId?: Id<"_storage">;
        additionalImages?: Array<{ storageId: Id<"_storage">; displayOrder: number }>;
      };
    }) => {
      setIsPending(true);
      try {
        const resultId = await updateMutation({
          id: id as Id<"portfolioProjects">,
          ...data,
        });
        toast({
          title: "Project updated",
        });
        return { id: resultId };
      } catch (error) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to update project",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    isPending,
  };
}
